/**
 * Alegerea departamentului unui task — aceeasi peste tot unde se trimite un task: „Task nou",
 * „Cer altui departament", „Nu e al nostru", Inbox, cererile de tipografie, recurente.
 *
 * Se pot bifa MAI MULTE si „Toate departamentele" (16.10.2026: „in caz ca Victor are ceva de
 * trimis catre toata lumea"). Pana pe 19.10.2026 doar adminii; de atunci oricine — Madalin:
 * „oricine atribuie oricui, e fair". Unde trebuie unul singur (sablonul editat), `multe={false}`.
 *
 * Macheta: https://claude.ai/artifact/FWm96uG1GE29Bfuk1NsWWE
 */
import { Check } from 'lucide-react'
import {
  DEPARTAMENTE,
  TOATE_DEPARTAMENTELE,
  numeDepartament,
  type DepartamentSlug,
} from '@b4l/shared'

/** „Vânzări, Marketing și Depozit". */
export function listaDepartamente(slugs: DepartamentSlug[]): string {
  const n = slugs.map(numeDepartament)
  return n.length <= 1 ? (n[0] ?? '') : `${n.slice(0, -1).join(', ')} și ${n[n.length - 1]}`
}

/** Textul butonului care trimite: „Trimite la Vânzări" / „Trimite la 3 departamente". */
export function trimiteLa(alese: DepartamentSlug[], verb = 'Trimite'): string {
  if (alese.length === 0) return 'Alege departamentul'
  if (alese.length === 1) return `${verb} la ${numeDepartament(alese[0]!)}`
  return `${verb} la ${alese.length} departamente`
}

export function AlegeDepartamente({
  alese,
  setAlese,
  alMeu = null,
  exclus = null,
  /** `false` forteaza un singur departament si la admin (de ex. editarea unui sablon). */
  multe: multePermis = true,
  eticheta = 'Departamentul',
}: {
  alese: DepartamentSlug[]
  setAlese: (d: DepartamentSlug[]) => void
  alMeu?: DepartamentSlug | null
  exclus?: DepartamentSlug | null
  multe?: boolean
  eticheta?: string
}) {
  const multe = multePermis
  const optiuni = DEPARTAMENTE.filter((d) => d.slug !== exclus)
  const toate = TOATE_DEPARTAMENTELE.filter((d) => d !== exclus)
  const toateBifate = toate.every((d) => alese.includes(d))

  const comuta = (d: DepartamentSlug) => {
    if (!multe) return setAlese([d])
    // Ordinea din lista, nu ordinea clicurilor: asa apar si in mesaje, si pe server.
    const urm = alese.includes(d) ? alese.filter((x) => x !== d) : [...alese, d]
    setAlese(optiuni.map((o) => o.slug).filter((s) => urm.includes(s)))
  }

  const stil = (activ: boolean) =>
    `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-mic focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-b4l-blue ${
      activ
        ? 'border-b4l-blue bg-b4l-blue-light font-semibold text-b4l-blue-dark'
        : 'border-b4l-border bg-b4l-surface text-b4l-text hover:border-b4l-blue'
    }`

  return (
    <div
      className="flex flex-wrap gap-1.5"
      role={multe ? 'group' : 'radiogroup'}
      aria-label={multe ? `${eticheta}, poți alege mai multe` : eticheta}
    >
      {multe && (
        <button
          type="button"
          role="checkbox"
          aria-checked={toateBifate}
          onClick={() =>
            setAlese(
              toateBifate
                ? alese.filter((a) => !toate.includes(a))
                : optiuni.map((o) => o.slug).filter((s) => toate.includes(s) || alese.includes(s)),
            )
          }
          className={`${stil(toateBifate)} border-dashed`}
        >
          Toate departamentele
        </button>
      )}
      {optiuni.map((d) => {
        const activ = alese.includes(d.slug)
        return (
          <button
            key={d.slug}
            type="button"
            role={multe ? 'checkbox' : 'radio'}
            aria-checked={activ}
            onClick={() => comuta(d.slug)}
            className={stil(activ)}
          >
            {multe && (
              <span
                aria-hidden="true"
                className={`grid h-3.5 w-3.5 place-items-center rounded-[3px] border ${
                  activ ? 'border-b4l-blue bg-b4l-blue text-white' : 'border-b4l-text-dim'
                }`}
              >
                {activ && <Check size={10} strokeWidth={3} />}
              </span>
            )}
            {d.nume}
            {d.slug === alMeu && ' · al tău'}
          </button>
        )
      })}
    </div>
  )
}
